import { useEffect, useState } from 'react';
import { Navigate } from 'react-router';
import { ApiError } from '../../lib/api';
import { operatorConsole, type Pack } from '../../lib/api/operatorConsole';

const STATUS_BADGE: Record<string, string> = {
  available: 'bg-emerald-100 text-emerald-800',
  restocking: 'bg-amber-100 text-amber-800',
};

export function PacksPage() {
  const [packs, setPacks] = useState<Pack[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [hidden, setHidden] = useState(false);

  useEffect(() => {
    const fetchPacks = async () => {
      try {
        setLoading(true);
        setError(null);
        const res = await operatorConsole.listPacks();
        setPacks(res.packs);
      } catch (err) {
        // 404 = the operator has no packs view (the backend hides the
        // feature entirely) — leave without a trace, like any unknown URL.
        if (err instanceof ApiError && err.status === 404) {
          setHidden(true);
          return;
        }
        setError(err instanceof Error ? err.message : 'Unknown error');
      } finally {
        setLoading(false);
      }
    };
    fetchPacks();
  }, []);

  if (hidden) return <Navigate to="/" replace />;
  if (loading) return <div className="p-6">Loading packs...</div>;
  if (error) return <div className="p-6 text-red-600">Error: {error}</div>;

  return (
    <div className="space-y-4 p-6">
      <div>
        <h1 className="text-2xl font-bold">Packs</h1>
        <p className="text-sm text-ui-text-secondary">
          Packs enabled for your platform — live stock and availability.
        </p>
      </div>

      {packs.length === 0 ? (
        <div className="rounded-lg border border-ui-border bg-ui-card p-6 text-center text-ui-text-secondary">
          No packs enabled for this operator
        </div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full border-collapse">
            <thead>
              <tr className="border-b border-ui-border">
                <th className="px-4 py-3 text-left text-sm font-semibold text-ui-text">ID</th>
                <th className="px-4 py-3 text-left text-sm font-semibold text-ui-text">Pack</th>
                <th className="px-4 py-3 text-left text-sm font-semibold text-ui-text">Price</th>
                <th className="px-4 py-3 text-left text-sm font-semibold text-ui-text">Stock</th>
                <th className="px-4 py-3 text-left text-sm font-semibold text-ui-text">Status</th>
              </tr>
            </thead>
            <tbody>
              {packs.map((pack) => (
                <tr key={pack.id} className="border-b border-ui-border">
                  <td className="px-4 py-3 font-mono text-sm text-ui-text-secondary">
                    #{pack.id}
                  </td>
                  <td className="px-4 py-3 text-sm font-medium text-ui-text">{pack.title}</td>
                  <td className="px-4 py-3 text-sm text-ui-text">${pack.price.toFixed(2)}</td>
                  <td className="px-4 py-3 text-sm text-ui-text">{pack.stock_count}</td>
                  <td className="px-4 py-3 text-sm">
                    <span
                      className={`rounded-full px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide ${
                        STATUS_BADGE[pack.status] ?? 'bg-ui-bg text-ui-text-secondary'
                      }`}
                    >
                      {pack.status}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
